import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import api from '../services/api';

// Types
interface Queue {
  _id: string;
  name: string;
  description?: string;
  locationIds: string[];
  priority: string;
  maxWaitTime: number;
  maxAgents: number;
  strategy: string;
  status: string;
  outboundPrefix?: string;
  agents: string[];
  sipNumbers: string[];
  assignedAgentIds: string[];
  assignedSipIds: string[];
  isActive: boolean;
  totalCalls?: number;
  avgWaitTime?: number;
  settings: any;
  statistics: any;
  customerCardApiUrl?: string;
  customerCardDatasetListUrl?: string;
  customerCardDatasetSchemaUrl?: string;
  customerCardDatasetAddUrl?: string;
  customerCardSelectedDatasetId?: string;
  createdAt: string;
  updatedAt: string;
}

interface QueueState {
  queues: Queue[];
  currentQueue: Queue | null;
  loading: boolean;
  error: string | null;
}

interface QueueAction {
  type: string;
  payload?: any;
}

interface QueueContextType {
  queues: Queue[];
  currentQueue: Queue | null;
  loading: boolean;
  error: string | null;
  fetchQueues: () => Promise<void>;
  fetchQueue: (id: string) => Promise<Queue | null>;
  createQueue: (queueData: Partial<Queue>) => Promise<Queue | null>;
  updateQueue: (id: string, queueData: Partial<Queue>) => Promise<Queue | null>;
  deleteQueue: (id: string) => Promise<boolean>;
  addSipNumberToQueue: (queueId: string, sipNumberId: string) => Promise<Queue | null>;
  removeSipNumberFromQueue: (queueId: string, sipNumberId: string) => Promise<Queue | null>;
  addAgentToQueue: (queueId: string, agentId: string) => Promise<Queue | null>;
  removeAgentFromQueue: (queueId: string, agentId: string) => Promise<Queue | null>;
  getQueueStatistics: (id: string, startDate?: string, endDate?: string) => Promise<any>;
  setCurrentQueue: (queue: Queue) => void;
  clearError: () => void;
}

// Queue Context
const QueueContext = createContext<QueueContextType | undefined>(undefined);

// Queue Reducer
const queueReducer = (state: QueueState, action: QueueAction): QueueState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_QUEUES':
      return { ...state, queues: action.payload, loading: false };
    case 'SET_CURRENT_QUEUE':
      return { ...state, currentQueue: action.payload };
    case 'ADD_QUEUE':
      return { ...state, queues: [...state.queues, action.payload] };
    case 'UPDATE_QUEUE':
      return {
        ...state,
        queues: state.queues.map((queue: Queue) =>
          queue._id === action.payload._id ? action.payload : queue
        )
      };
    case 'DELETE_QUEUE':
      return {
        ...state,
        queues: state.queues.filter((queue: Queue) => queue._id !== action.payload)
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Initial State
const initialState = {
  queues: [],
  currentQueue: null,
  loading: false,
  error: null
};

// Queue Provider
interface QueueProviderProps {
  children: ReactNode;
}

export const QueueProvider = ({ children }: QueueProviderProps) => {
  const [state, dispatch] = useReducer(queueReducer, initialState);


  // Set loading
  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  // Set error
  const setError = (error: string) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Fetch all queues
  const fetchQueues = async (filters = {}) => {
    try {
      console.log('🔄 QueueContext: Starting fetchQueues...');
      setLoading(true);
      clearError();

      const params = new URLSearchParams(filters);
      
      const response = await api.get(`/queues?${params}`);

      console.log('🔍 QueueContext: API response:', response.data);

      if (response.data.success) {
        console.log('✅ QueueContext: Queues set successfully:', response.data.data);
        dispatch({ type: 'SET_QUEUES', payload: response.data.data });
      } else {
        console.error('❌ QueueContext: API returned error:', response.data.message);
        setError(response.data.message || 'Kuyruklar getirilemedi');
      }
    } catch (error) {
      console.error('❌ QueueContext: Kuyruklar getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Kuyruklar getirilemedi');
    }
  };

  // Fetch single queue
  const fetchQueue = async (id: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.get(`/queues/${id}`);

      if (response.data.success) {
        dispatch({ type: 'SET_CURRENT_QUEUE', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Kuyruk getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('Kuyruk getirme hatası:', error);
      setError((error as any).response?.data?.message || 'Kuyruk getirilemedi');
      return null;
    }
  };

  // Create queue
  const createQueue = async (queueData: Partial<Queue>) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.post(`/queues`, queueData);

      if (response.data.success) {
        dispatch({ type: 'ADD_QUEUE', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Kuyruk oluşturulamadı');
        return null;
      }
    } catch (error) {
      console.error('Kuyruk oluşturma hatası:', error);
      setError((error as any).response?.data?.message || 'Kuyruk oluşturulamadı');
      return null;
    }
  };

  // Update queue
  const updateQueue = async (id: string, queueData: Partial<Queue>) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.put(`/queues/${id}`, queueData);

      if (response.data.success) {
        dispatch({ type: 'UPDATE_QUEUE', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'Kuyruk güncellenemedi');
        return null;
      }
    } catch (error) {
      console.error('Kuyruk güncelleme hatası:', error);
      setError((error as any).response?.data?.message || 'Kuyruk güncellenemedi');
      return null;
    }
  };

  // Delete queue
  const deleteQueue = async (id: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.delete(`/queues/${id}`);

      if (response.data.success) {
        dispatch({ type: 'DELETE_QUEUE', payload: id });
        return true;
      } else {
        setError(response.data.message || 'Kuyruk silinemedi');
        return false;
      }
    } catch (error) {
      console.error('Kuyruk silme hatası:', error);
      setError((error as any).response?.data?.message || 'Kuyruk silinemedi');
      return false;
    }
  };

  // Add SIP number to queue
  const addSipNumberToQueue = async (queueId: string, sipNumberId: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.post(`/queues/${queueId}/sip-numbers`, {
        sipNumberId
      });

      if (response.data.success) {
        // Update the queue in state
        const updatedQueue = response.data.data;
        dispatch({ type: 'UPDATE_QUEUE', payload: updatedQueue });
        return updatedQueue;
      } else {
        setError(response.data.message || 'SIP numarası kuyruğa eklenemedi');
        return null;
      }
    } catch (error) {
      console.error('SIP numarası ekleme hatası:', error);
      setError((error as any).response?.data?.message || 'SIP numarası kuyruğa eklenemedi');
      return null;
    }
  };

  // Remove SIP number from queue
  const removeSipNumberFromQueue = async (queueId: string, sipNumberId: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.delete(`/queues/${queueId}/sip-numbers/${sipNumberId}`);

      if (response.data.success) {
        // Update the queue in state
        const updatedQueue = response.data.data;
        dispatch({ type: 'UPDATE_QUEUE', payload: updatedQueue });
        return updatedQueue;
      } else {
        setError(response.data.message || 'SIP numarası kuyruktan çıkarılamadı');
        return null;
      }
    } catch (error) {
      console.error('SIP numarası çıkarma hatası:', error);
      setError((error as any).response?.data?.message || 'SIP numarası kuyruktan çıkarılamadı');
      return null;
    }
  };

  // Add agent to queue
  const addAgentToQueue = async (queueId: string, agentId: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.post(`/queues/${queueId}/agents`, {
        agentId
      });

      if (response.data.success) {
        // Update the queue in state
        const updatedQueue = response.data.queue;
        dispatch({ type: 'UPDATE_QUEUE', payload: updatedQueue });
        return updatedQueue;
      } else {
        setError(response.data.message || 'Agent kuyruğa eklenemedi');
        return null;
      }
    } catch (error) {
      console.error('Agent ekleme hatası:', error);
      setError((error as any).response?.data?.message || 'Agent kuyruğa eklenemedi');
      return null;
    }
  };

  // Remove agent from queue
  const removeAgentFromQueue = async (queueId: string, agentId: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.delete(`/queues/${queueId}/agents/${agentId}`);

      if (response.data.success) {
        // Update the queue in state
        const updatedQueue = response.data.queue;
        dispatch({ type: 'UPDATE_QUEUE', payload: updatedQueue });
        return updatedQueue;
      } else {
        setError(response.data.message || 'Agent kuyruktan çıkarılamadı');
        return null;
      }
    } catch (error) {
      console.error('Agent çıkarma hatası:', error);
      setError((error as any).response?.data?.message || 'Agent kuyruktan çıkarılamadı');
      return null;
    }
  };

  // Get queue statistics
  const getQueueStatistics = async (id: string, startDate?: string, endDate?: string) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const response = await api.get(`/queues/${id}/stats?${params}`);

      if (response.data.success) {
        return response.data;
      } else {
        setError(response.data.message || 'İstatistikler getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('İstatistik getirme hatası:', error);
      setError((error as any).response?.data?.message || 'İstatistikler getirilemedi');
      return null;
    }
  };

  // Set current queue
  const setCurrentQueue = (queue: Queue) => {
    dispatch({ type: 'SET_CURRENT_QUEUE', payload: queue });
  };

  // Context value
  const value = {
    ...state,
    fetchQueues,
    fetchQueue,
    createQueue,
    updateQueue,
    deleteQueue,
    addSipNumberToQueue,
    removeSipNumberFromQueue,
    addAgentToQueue,
    removeAgentFromQueue,
    getQueueStatistics,
    setCurrentQueue,
    setLoading,
    setError,
    clearError
  };

  return (
    <QueueContext.Provider value={value}>
      {children}
    </QueueContext.Provider>
  );
};

// Queue Hook
export const useQueue = (): QueueContextType => {
  const context = useContext(QueueContext);
  if (!context) {
    throw new Error('useQueue must be used within a QueueProvider');
  }
  return context;
};

export default QueueContext;
